import { cn } from '@/shared/lib'

type FieldOutlineProps = {
  /** Must match the visible label exactly — it reserves the gap for it. */
  label: string
  /** Notch permanently. Off means it opens only once the field is focused or filled. */
  isRaised?: boolean
  tone?: keyof typeof TONES
  /**
   * Where the gap starts, in px from the field's left edge. Defaults to the
   * corner radius so the straight run of the top border begins with the gap;
   * a leading icon pushes the label — and therefore this — to the right.
   */
  notchInset?: number
  className?: string
}

const TONES = {
  normal: 'border-input group-focus-within/field:border-ring',
  error: 'border-destructive/50',
  success: 'border-success/50 group-focus-within/field:border-ring',
  disabled: 'border-border-disabled',
} as const

const GLOW = {
  normal: 'group-focus-within/field:border-primary/13',
  error: 'group-focus-within/field:border-destructive/20',
  success: 'group-focus-within/field:border-primary/13',
  disabled: '',
} as const

/*
 * The gap is deliberately wider than the label: `px-2` against the label's
 * `px-1.5` leaves 2px of slack on each side, so sub-pixel rounding can never
 * leave a lit sliver of border showing under the text.
 *
 * The gap starts at x = 10, exactly where the corner arc ends, so the straight
 * run of the top border begins with the gap instead of a two-pixel stub.
 * The margins differ per layer because each legend is measured from inside its
 * own border — 1px on the border layer, 3px on the glow layer, which also sits
 * 3px further out. Both land the gap at the same x.
 */
const LEGEND = 'invisible h-0 p-0 text-label whitespace-nowrap transition-all duration-150 ease-out'

/** Closed at rest; opens once the field is focused or has a value. */
const NOTCH_ON_USE =
  'max-w-0 group-has-[input:not(:placeholder-shown)]/field:max-w-full group-has-[input:not(:placeholder-shown)]/field:px-2 group-has-[input:focus]/field:max-w-full group-has-[input:focus]/field:px-2'

/**
 * The field's border, drawn as a `fieldset` so its `legend` can cut a real gap
 * for the label.
 *
 * Painting a background behind the label instead would only work while the
 * field sits directly on the canvas, and it reads as a chip stuck onto the
 * border. A legend removes that stretch of border outright, so the label needs
 * no backdrop and the field can sit on a card, a toolbar or a modal with
 * nothing to keep in sync.
 *
 * The focus ring is a **second** notched fieldset rather than a `ring-*`
 * utility: a box-shadow traces the whole rounded rectangle and would run
 * straight through the gap, behind the label. Drawing it as a 3px border on an
 * outer fieldset lets it carry the same notch. Its margin compensates for the
 * layer's 3px offset and 3px border so both gaps start at the same x.
 *
 * Both layers are `aria-hidden` decoration — the real label is already
 * associated with the control.
 */
export function FieldOutline({
  label,
  isRaised = false,
  tone = 'normal',
  notchInset = 10,
  className,
}: FieldOutlineProps) {
  const notch = isRaised ? 'max-w-full px-2' : NOTCH_ON_USE

  return (
    <>
      <fieldset
        aria-hidden
        className={cn(
          'pointer-events-none absolute -inset-[3px] m-0 rounded-[13px] border-[3px] border-transparent p-0 transition-colors',
          GLOW[tone],
        )}
      >
        <legend className={cn(LEGEND, notch)} style={{ marginInlineStart: notchInset }}>
          {label}
        </legend>
      </fieldset>

      <fieldset
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 m-0 rounded-lg border p-0 transition-colors',
          TONES[tone],
          className,
        )}
      >
        <legend className={cn(LEGEND, notch)} style={{ marginInlineStart: notchInset - 1 }}>
          {label}
        </legend>
      </fieldset>
    </>
  )
}
